/**
 * Sample rate per key user action. A root span carrying `KEY_FLOW_ATTRIBUTE` is sampled at its
 * flow's rate instead of the app's page rate, and the backends keep that decision downstream.
 * `join` is ~95% of the volume, so it starts below 1 (#239).
 */
export const KEY_FLOW_RATES = {
  join: 0.25,
  pay: 1,
  accept_seats: 1,
  guest_invitation: 1,
} as const;

export type KeyFlow = keyof typeof KEY_FLOW_RATES;

export const KEY_FLOW_ATTRIBUTE = 'seaters.key_flow';

/** The part of Sentry's `TracesSamplerSamplingContext` the sampler reads; keeps this file Sentry-free. */
interface KeyFlowSamplingContext {
  attributes?: Record<string, unknown>;
  inheritOrSampleWith: (fallbackSampleRate: number) => number;
}

export const createKeyFlowTracesSampler =
  (defaultRate: number) =>
  ({ attributes, inheritOrSampleWith }: KeyFlowSamplingContext): number => {
    const flow = attributes?.[KEY_FLOW_ATTRIBUTE];
    if (typeof flow === 'string' && Object.hasOwn(KEY_FLOW_RATES, flow)) {
      return KEY_FLOW_RATES[flow as KeyFlow];
    }
    return inheritOrSampleWith(defaultRate);
  };

/**
 * The build's own API origin, the only host that should get `sentry-trace` + `baggage`. Derived
 * from `SEATERS_API_BASE_URL` so a new environment cannot be missed again. Empty when unset (tests).
 */
export const apiTracePropagationTargets = (apiBaseUrl?: string): RegExp[] => {
  if (!apiBaseUrl) return [];
  const origin = new URL(apiBaseUrl).origin.replace(
    /[.*+?^${}()|[\]\\]/g,
    '\\$&'
  );
  return [new RegExp(`^${origin}(/|$)`)];
};

/**
 * Transaction name for the invitation and guest-list apps, which have no route table: the slug and
 * waiting-list id leave the path, so transactions group by page instead of one per invitation.
 */
export const guestAppTransactionName = (pathname: string): string =>
  pathname
    .replace(/^\/[^/]+/, '/:slug')
    .replace(/\/waitinglist\/[^/]+/, '/waitinglist/:waitingListId');
